import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { encryptIdForUrl } from "@/utils/urlCrypto";
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit2,
  UserX,
  Shield,
  Mail,
  Phone,
  Calendar,
  Clock,
  FileText,
  RefreshCw,
  UserPlus,
  Loader2,
  Trash2,
  CheckCircle,
  SearchX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { useAgentesStore } from "@/stores/agentesStore";
import { AgenteCreateForm } from "@/components/agente/AgenteCreateForm";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useTheme } from "@/hooks/use-theme";
import { AppConfig } from "@/datasources/AppConfig";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { useToast } from "@/hooks/use-toast";

const papelConfig: Record<string, { label: string; className: string }> = {
  gestor: {
    label: "Gestor",
    className: "bg-primary/10 text-primary border border-primary/20",
  },
  operador: {
    label: "Operador",
    className: "bg-secondary/10 text-secondary border border-secondary/20",
  },
  visualizador: {
    label: "Visualizador",
    className: "bg-muted text-muted-foreground border border-border",
  },
};

const statusConfig: Record<string, { label: string; className: string }> = {
  "Aprovado": {
    label: "Aprovado",
    className: "bg-success/10 text-success",
  },
  "Reprovado": {
    label: "Reprovado",
    className: "bg-black text-white dark:bg-white dark:text-black",
  },
  "Aguardando Aprovação": {
    label: "Aguardando aprovação",
    className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  },
};

export default function AgentesPage() {
  const navigate = useNavigate();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const observerTarget = useRef<HTMLDivElement | null>(null);
  const {
    agentes,
    isLoading,
    error,
    fetchAgentes,
    totalAprovados,
    totalAguardandoAprovacao,
    totalReprovados,
    isLoadingCounts,
    solicitacoesPorAgente,
    isLoadingSolicitacoesPorAgente,
    fetchAgentesCounts,
    fetchSolicitacoesPorAgente,
    pageNumber,
    hasMore,
    isLoadingMore,
    resetPagination,
  } = useAgentesStore();
  const { isReadOnly, isGestor } = useAgentePermissions();
  const { theme } = useTheme();
  const { toast } = useToast();
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const handleAgenteClick = async (id: string) => {
    const encryptedId = await encryptIdForUrl(id);
    navigate(`/agentes/${encryptedId}`);
  };

  const handleDeleteAgente = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este agente? Esta ação não pode ser desfeita.")) {
      return;
    }

    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.delete({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${id}`,
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao excluir agente",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao excluir o agente.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Agente excluído",
        description: "O agente foi excluído com sucesso.",
      });

      // Refresh the list
      resetPagination();
      fetchAgentes(statusFilter, 1, true);
      fetchAgentesCounts();
    } catch (error) {
      toast({
        title: "Erro ao excluir agente",
        description: error instanceof Error ? error.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    }
  };

  const handleChangeStatus = async (id: string, status: string) => {
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.post({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${id}/alterar-status`,
        queryParams: { status },
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao alterar status",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao alterar o status.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Status alterado",
        description: `O status do agente foi alterado para ${status}.`,
      });

      // Refresh the list
      resetPagination();
      fetchAgentes(statusFilter, 1, true);
      fetchAgentesCounts();
    } catch (error) {
      toast({
        title: "Erro ao alterar status",
        description: error instanceof Error ? error.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    }
  };

  const handleRestoreAgente = async (id: string) => {
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.post({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${id}/restaurar`,
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao restaurar agente",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao restaurar o agente.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Agente restaurado",
        description: "O agente foi restaurado com sucesso.",
      });

      // Refresh the list
      resetPagination();
      fetchAgentes(statusFilter, 1, true);
      fetchAgentesCounts();
    } catch (error) {
      toast({
        title: "Erro ao restaurar agente",
        description: error instanceof Error ? error.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    }
  };

  const handleAtribuirGestor = async (id: string) => {
    if (!confirm("Tem certeza que deseja atribuir o papel de gestor a este agente?")) {
      return;
    }

    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.post({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${id}/atribuir-gestor`,
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
        body: {},
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao atribuir gestor",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao atribuir gestor.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Gestor atribuído",
        description: "O agente foi promovido a gestor com sucesso.",
      });

      // Refresh the list
      resetPagination();
      fetchAgentes(statusFilter, 1, true);
      fetchAgentesCounts();
    } catch (error) {
      toast({
        title: "Erro ao atribuir gestor",
        description: error instanceof Error ? error.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    }
  };

  // Initial fetch
  useEffect(() => {
    resetPagination();
    fetchAgentes(statusFilter, 1, true);
  }, [fetchAgentes, resetPagination, statusFilter]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega a lista
  useEffect(() => {
    const handleFocus = () => {
      resetPagination();
      fetchAgentes(statusFilter, 1, true);
      fetchAgentesCounts();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        resetPagination();
        fetchAgentes(statusFilter, 1, true);
        fetchAgentesCounts();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchAgentes, resetPagination, statusFilter, fetchAgentesCounts]);

  // Fetch total counts for approved and pending agents (independent of filter)
  useEffect(() => {
    fetchAgentesCounts();
  }, [fetchAgentesCounts]);

  // Fetch solicitation counts for each agent when the agent list changes
  useEffect(() => {
    if (agentes.length > 0) {
      const agenteIds = agentes.map((a) => a.id);
      fetchSolicitacoesPorAgente(agenteIds);
    }
  }, [agentes, fetchSolicitacoesPorAgente]);

  // Infinite scroll observer
  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoading) {
      fetchAgentes(statusFilter, pageNumber + 1, false);
    }
  }, [hasMore, isLoadingMore, isLoading, pageNumber, statusFilter, fetchAgentes]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore, hasMore, isLoadingMore, isLoading]);

  // Filtrar agentes por nome (status é filtrado no servidor)
  const filteredAgentes = agentes
    .filter((agente) => {
      const matchesSearch = agente.nome.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .slice()
    .sort((a, b) => {
      if (a.id === AppConfig.agenteId) return -1;
      if (b.id === AppConfig.agenteId) return 1;
      return 0;
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar agentes..." 
              className="pl-10" 
              disabled={isLoading}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter} disabled={isLoading}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="Aprovado">Aprovados</SelectItem>
              <SelectItem value="Reprovado">Reprovados</SelectItem>
              <SelectItem value="AguardandoAprovacao">Aguardando aprovação</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => fetchAgentes(statusFilter)} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          {!isReadOnly && (
            <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Novo Agente
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" preventCloseOnOutsideClick={true}>
                <AgenteCreateForm
                  onSuccess={() => {
                    setIsInviteOpen(false);
                    resetPagination();
                    fetchAgentes(statusFilter, 1, true);
                    fetchAgentesCounts();
                  }}
                  onCancel={() => setIsInviteOpen(false)}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="surface-elevated p-4">
          <p className="text-caption">Total de Agentes</p>
          <p className="kpi-value mt-1">{filteredAgentes.length}</p>
        </div>
        <div className="surface-elevated p-4" style={{ backgroundColor: '#22c55e14', borderColor: '#22c55e33' }}>
          <p className="text-caption">Aprovados</p>
          <p className="kpi-value mt-1" style={{ color: '#22c55e' }}>
            {isLoadingCounts ? '-' : totalAprovados}
          </p>
        </div>
        <div className="surface-elevated p-4" style={{ backgroundColor: '#ef444414', borderColor: '#ef444433' }}>
          <p className="text-caption">Aguardando aprovação</p>
          <p className="kpi-value mt-1" style={{ color: '#ef4444' }}>
            {isLoadingCounts ? '-' : totalAguardandoAprovacao}
          </p>
        </div>
        <div className="surface-elevated p-4" style={{ backgroundColor: `${isDark ? '#ffffff' : '#000000'}14`, borderColor: `${isDark ? '#ffffff' : '#000000'}33` }}>
          <p className="text-caption">Reprovados</p>
          <p className="kpi-value mt-1" style={{ color: isDark ? '#ffffff' : '#000000' }}>
            {isLoadingCounts ? '-' : totalReprovados}
          </p>
        </div>
      </div>

      {/* Agents Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="surface-elevated p-5 animate-pulse h-44 rounded-xl border" />
          ))}
        </div>
      ) : filteredAgentes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 space-y-6">
          <div className="relative w-48 h-48 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-muted/50 animate-pulse" />
            <SearchX className="relative w-20 h-20 text-muted-foreground/60 animate-bounce" style={{ animationDuration: '2s' }} />
          </div>
          <div className="text-center space-y-3 max-w-md">
            <h3 className="text-xl font-semibold text-foreground">
              {searchTerm || statusFilter !== "all" ? "Nenhum agente encontrado" : "Parece que você está sozinho!"}
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              {searchTerm || statusFilter !== "all" 
                ? "Tente ajustar os filtros de busca ou status para encontrar o agente que procura."
                : "Você está gerenciando todas as solicitações sozinho. Para não ficar sobrecarregado, convide um agente da sua ouvidoria para participar dessa gestão, agilizar o processo e deixar a cidade impecável!"
              }
            </p>
          </div>
          {(!searchTerm && statusFilter === "all" && !isReadOnly) && (
            <button
              onClick={() => setIsInviteOpen(true)}
              className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 100%)",
                animation: "glow-pulse 2s ease-in-out infinite",
              }}
            >
              <UserPlus className="h-5 w-5" />
              <span>Novo Agente</span>
              <span
                className="absolute -inset-1 rounded-full opacity-75 blur-sm -z-10"
                style={{
                  background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 100%)",
                  animation: "glow-pulse 2s ease-in-out infinite",
                }}
              />
              <style>{`
                @keyframes glow-pulse {
                  0%, 100% {
                    opacity: 1;
                    box-shadow: 0 0 20px hsl(var(--primary) / 0.6), 0 0 40px hsl(var(--secondary) / 0.4);
                  }
                  50% {
                    opacity: 0.8;
                    box-shadow: 0 0 30px hsl(var(--primary) / 0.8), 0 0 60px hsl(var(--secondary) / 0.6);
                  }
                }
              `}</style>
            </button>
          )}
        </div>
      ) : (
        <>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredAgentes.map((agente) => {
            const papel = papelConfig[agente.papel || ""] || {
              label: agente.papel || "N/A",
              className: "bg-muted text-muted-foreground border border-border",
            };
            const status = statusConfig[agente.status || ""] || {
              label: agente.status || "N/A",
              className: "bg-muted text-muted-foreground",
            };
            const avatarFallback = agente.nome.slice(0, 2).toUpperCase();
            const slaClass =
              agente.slaMedia && agente.slaMedia > 0
                ? agente.slaMedia >= 95
                  ? "text-success"
                  : agente.slaMedia >= 90
                    ? "text-warning"
                    : "text-destructive"
                : "text-muted-foreground";

            return (
              <div
                key={agente.id}
                onClick={() => handleAgenteClick(agente.id)}
                className="surface-elevated p-5 animate-fade-in hover:shadow-elevated transition-shadow rounded-xl border cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar
                      className={cn(
                        "h-12 w-12 font-heading font-semibold",
                        agente.status === "Aprovado" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {agente.fotoPerfil ? (
                        <AvatarImage src={resolveMediaUrl(agente.fotoPerfil.arquivo) || agente.fotoPerfil.arquivo} alt={agente.nome} />
                      ) : null}
                      <AvatarFallback>{avatarFallback}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-medium">
                        {agente.id === AppConfig.agenteId ? `Você (${agente.nome})` : agente.nome}
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", papel.className)}>
                          {papel.label}
                        </span>
                        {agente.gestor && (
                          <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 text-primary border border-primary/30 font-semibold text-xs px-2 py-0.5 shadow-sm">
                            <Shield className="h-3 w-3 mr-1 text-primary" />
                            Gestor
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isReadOnly && agente.id !== AppConfig.agenteId && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Shield className="h-4 w-4 mr-2" />
                            Alterar Status
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem onClick={() => handleChangeStatus(agente.id, "Aguardando Aprovação")}>
                              Aguardando Aprovação
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeStatus(agente.id, "Aprovado")}>
                              Aprovado
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeStatus(agente.id, "Reprovado")}>
                              Reprovado
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        {isGestor && !agente.gestor && (
                          <DropdownMenuItem onClick={() => handleAtribuirGestor(agente.id)}>
                            <Shield className="h-4 w-4 mr-2" />
                            Atribuir Gestor
                          </DropdownMenuItem>
                        )}
                        {agente.arquivado && (
                          <DropdownMenuItem onClick={() => handleRestoreAgente(agente.id)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Restaurar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteAgente(agente.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  {agente.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground truncate">{agente.email}</span>
                    </div>
                  )}
                  {agente.telefone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{agente.telefone}</span>
                    </div>
                  )}
                  {agente.dataIngresso && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Desde {agente.dataIngresso}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", status.className)}>
                      {status.label}
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">
                          {isLoadingSolicitacoesPorAgente 
                            ? '-' 
                            : solicitacoesPorAgente[agente.id] ?? 0}
                        </span>
                      </div>
                      {agente.slaMedia !== undefined && agente.slaMedia > 0 && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className={cn("font-medium", slaClass)}>{agente.slaMedia}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {isLoadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        <div ref={observerTarget} className="h-1" />
        </>
      )}
    </div>
  );
}
